import type { UnifiedSim } from '../../services/adminService';

/**
 * KAN-105: inventory connectivity types.
 *
 * The inventory used to be one table filtered by a Provider dropdown. That
 * stopped working once Starlink arrived: a terminal has no ICCID, no MSISDN and
 * no SIM status, and instead needs Kit S/N, Site, Service Line and an online
 * state. A union-of-columns table would have been mostly empty cells, and the
 * empties read as missing data rather than "not applicable".
 *
 * So the page is organised around a type selector instead, and each type owns
 * its own column set, filters and row actions. This module is the single source
 * of truth for what a type IS — the page reads it, so adding a fourth type
 * later means adding an entry here rather than another branch in the table.
 */

export type InventoryTypeKey = 'crew' | 'data' | 'esim' | 'starlink';

export interface InventoryType {
  key: InventoryTypeKey;
  /** Tab label. Kept short — the count is appended at render time. */
  label: string;
  /** Longer description surfaced in the page help. */
  description: string;
  /** Provider this type maps to. */
  provider: string;
  /** Narrows within a provider (eSIM vs physical); applied client-side. */
  predicate: (sim: UnifiedSim) => boolean;
  /** Column keys rendered for this type, in order. */
  columns: string[];
  /** Which filter controls make sense for this type. */
  filters: Array<'status' | 'online' | 'allocation'>;
  /** Placeholder for the search box — the searchable fields differ per type. */
  searchPlaceholder: string;
  /** Whether rows link through to a detail page. */
  hasDetailPage: boolean;
}

const isTerminal = (sim: UnifiedSim) => sim.provider === 'clarus';

/**
 * Column sets.
 *
 * KAN-110 removed the "All" type. A combined list could only show the fields
 * every type shares, which is barely more than the identifier — the per-type
 * tabs are where the inventory is actually read. Crew SIMs, the largest and
 * most-used set, is the default; it is first here and getInventoryType falls
 * back to it.
 */
export const INVENTORY_TYPES: InventoryType[] = [
  {
    key: 'crew',
    label: 'Crew SIMs',
    description: 'UK mobile SIMs issued to crew phones (Giacom).',
    provider: 'giacom',
    predicate: sim => sim.provider === 'giacom',
    columns: [
      'identifier',
      'msisdn',
      'status',
      'alias',
      'friendlyName',
      'allocatedTo',
      'assignedTo',
      'actions',
    ],
    filters: ['status', 'allocation'],
    searchPlaceholder: 'Search by ICCID, MSISDN, alias, friendly name…',
    hasDetailPage: false,
  },
  {
    key: 'data',
    label: 'Data SIMs',
    description: 'Physical maritime data SIMs for vessel connectivity (VRS).',
    provider: 'vrs',
    predicate: sim => sim.provider === 'vrs' && !sim.isEsim,
    columns: [
      'identifier',
      'msisdn',
      'status',
      'alias',
      'friendlyName',
      'allocatedTo',
      'assignedTo',
      'actions',
    ],
    filters: ['status', 'allocation'],
    searchPlaceholder: 'Search by ICCID, MSISDN, alias, friendly name…',
    hasDetailPage: true,
  },
  {
    key: 'esim',
    label: 'eSIMs',
    description:
      'Downloadable eSIM profiles, including Travel eSIMs. Shows the eUICC ID and provisioning state.',
    provider: 'vrs',
    predicate: sim => sim.provider === 'vrs' && !!sim.isEsim,
    columns: [
      'identifier',
      'msisdn',
      'status',
      'eid',
      'rspState',
      'customReference',
      'friendlyName',
      'createdAt',
      'allocatedTo',
      'assignedTo',
      'actions',
    ],
    filters: ['status', 'allocation'],
    searchPlaceholder: 'Search by ICCID, MSISDN, alias, friendly name…',
    hasDetailPage: true,
  },
  {
    key: 'starlink',
    label: 'Starlink',
    description:
      'Starlink terminals (Clarus/Aavora). Online state comes from the hourly sync; open a terminal for live telemetry and data usage.',
    provider: 'clarus',
    predicate: isTerminal,
    columns: [
      'deviceId',
      'kitSerial',
      'site',
      'serviceLine',
      'online',
      'friendlyName',
      'allocatedTo',
      'assignedTo',
      'actions',
    ],
    filters: ['online', 'allocation'],
    searchPlaceholder: 'Search by device ID, kit/dish serial, site, service line…',
    hasDetailPage: true,
  },
];

/** The tab shown when the URL names none — first in the list above. */
export const DEFAULT_TYPE_KEY: InventoryTypeKey = INVENTORY_TYPES[0].key;

export const getInventoryType = (key: string | null | undefined): InventoryType =>
  INVENTORY_TYPES.find(t => t.key === key) || INVENTORY_TYPES[0];

/** Count per type for the tab labels, computed from a single fetched list. */
export const countByType = (sims: UnifiedSim[]): Record<InventoryTypeKey, number> => {
  const counts = {} as Record<InventoryTypeKey, number>;
  for (const type of INVENTORY_TYPES) {
    counts[type.key] = sims.filter(type.predicate).length;
  }
  return counts;
};

/**
 * True when a type key is one this build knows about. Guards the value read
 * from the URL, so a stale or hand-edited `?type=` — including `?type=all`
 * from a link saved before KAN-110 — falls back to Crew SIMs rather than
 * rendering an empty table with no columns.
 */
export const isValidTypeKey = (key: string | null | undefined): key is InventoryTypeKey =>
  INVENTORY_TYPES.some(t => t.key === key);
